import React from "react";
import { Row, Col, Switch, Tooltip, Typography } from "antd";
import { LuInfo } from "react-icons/lu";
import CustomButton from "../../commonComponents/button";
import CustomDivider from "../../commonComponents/divider";
import { formatPhoneDisplay } from "../../../utils/phoneHelpers";
import { checkAccessPermission } from "../../../library/helpers";
import { AccessPermissionAction } from "../../../library/constants";
import ReadOnlyField from "./ReadOnlyField";

const { Text } = Typography;

const ProfileTab = ({
  user,
  profileData,
  languages,
  onEdit,
  skipArchiveRestoreConfirm,
  onToggleSkipArchive,
}) => {
  const phoneDisplayData = formatPhoneDisplay(user);
  const canEditProfile = checkAccessPermission(
    AccessPermissionAction.USER_PROFILE_USER_INFORMATION
  );

  const preferredLanguage =
    languages.find(l => l.languageCode === user?.preferredLanguageCode)?.languageName ||
    user?.preferredLanguageCode ||
    "--";

  // Custom cell: Phone(s) with per-number tooltips
  const renderPhones = () => (
    <div className="mb-8">
      <Text type="secondary" className="text-xs">
        Phone(s)
      </Text>
      <div className="mt-2">
        {phoneDisplayData.values && phoneDisplayData.values.length > 0 ? (
          phoneDisplayData.values.map((phoneValue, index) => (
            <div key={index} className={index < phoneDisplayData.values.length - 1 ? "mb-1" : ""}>
              <span className="text-black-88 text-sm font-normal">{phoneValue.number}</span>{" "}
              <span className="text-black-65 text-xs font-normal">{phoneValue.rest}</span>{" "}
              <Tooltip
                title={phoneDisplayData.tooltips?.[index] || "Phone number information"}
                placement="top"
              >
                <LuInfo className="text-black-45 cursor-help inline-block align-middle ml-1" />
              </Tooltip>
            </div>
          ))
        ) : (
          <Text strong>--</Text>
        )}
      </div>
    </div>
  );

  // Custom section body: Additional Setting toggles
  const additionalSettings = (
    <div className="mb-4">
      {/* Display-only here — edit via "Edit" (profile drawer), saved to server */}
      <Tooltip title="Change this from the Edit profile form">
        <span className="font-normal inline-flex items-center cursor-not-allowed">
          <Switch checked={user?.isTranslationEnabled} size="small" disabled />
          <span className="ml-2">Do not translate</span>
        </span>
      </Tooltip>
      <div className="mt-4 font-normal">
        <Switch size="small" checked={skipArchiveRestoreConfirm} onChange={onToggleSkipArchive} />
        <span className="ml-2">Skip archive &amp; restore confirmation</span>
        <p className="text-xs text-black/[0.45] mt-1 mb-0 ml-9">
          When enabled, <strong>archiving</strong> or <strong>restoring</strong> a lead will happen
          immediately without showing the confirmation dialog. This preference is saved to this
          browser only.
        </p>
      </div>
    </div>
  );

  // Section-wise config: add a field/section here and it renders automatically
  const sections = [
    {
      title: "Personal Information",
      action: <CustomButton handleClick={onEdit} btnText="Edit" disabled={!canEditProfile} />,
      fields: [
        { label: "First name", value: profileData.firstName },
        { label: "Last name", value: profileData.lastName },
        { label: "Preferred name", value: profileData.preferredName, optional: true },
        { label: "Company role", value: profileData.companyRole },
        { label: "Email address", value: profileData.email },
        { label: "Phone(s)", render: renderPhones },
      ],
    },
    {
      title: "Other Information",
      fields: [{ label: "Preferred language", value: preferredLanguage }],
    },
    {
      title: "Additional Setting",
      content: additionalSettings,
    },
  ];

  return (
    <div className="pt-8 px-4">
      {sections.map((section, index) => (
        <React.Fragment key={section.title}>
          {index > 0 && <CustomDivider className="!my-6" />}
          <div>
            <div className="flex justify-between items-center mb-8">
              <p className="text-black-88 font-semibold text-base">{section.title}</p>
              {section.action || null}
            </div>

            {section.content ? (
              section.content
            ) : (
              <Row gutter={[16, 0]} className="max-w-[720px]">
                {section.fields.map(field => (
                  <Col key={field.label} xs={24} sm={12}>
                    {field.render ? (
                      field.render()
                    ) : (
                      <ReadOnlyField
                        label={field.label}
                        value={field.value}
                        optional={field.optional}
                      />
                    )}
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

export default ProfileTab;
